<template>
  <v-list-item-content>
    <div class="item-header">
      <span><notification-icon-content :item="item" /> &nbsp;{{ item.ShortDesc }}</span
      ><br />
      <span class="owner-label"> <notification-owner-content :item="item" :isOwner="isNotificationCreator(item)" /> </span>
    </div>
    <div class="item-content">{{ item.Text }}</div>
    <div v-if="item.Replies && item.Replies.length && item.showReplyItems" class="item-replies">
      <hr />
      <br />
      <div v-for="reply in item.Replies" :key="reply.Id" style="display: flow-root">
        <p class="reply-item" :class="`${showAsCreator(item, reply) ? 'provider' : ''}`">
          <span>{{ reply.Text }}</span
          ><br />
          <span class="owner-label"> <notification-owner-content :item="item" :reply="reply" :isOwner="isReplyCreator(item, reply)" /> </span>
        </p>
      </div>
    </div>
    <br />

    <div class="reply-actions text-right">
      <v-icon v-if="isNotificationCreator(item)" title="Withdraw Notification" class="" @click="withdrawNotification(item)"> mdi-bell-off </v-icon>
      <notification-redirect-content :item="item" :userId="userId" :isTeamMember="isTeamMember" :isAdmin="isAdmin" :isClient="isClient" />
      <span v-if="item.RepliesCount" @click="toggleReplyViewItems(item)" class="">
        <span class="reply-counter-number" v-if="notificationItemNotSeenRepliesCount(item) > 0">{{ notificationItemNotSeenRepliesCount(item) }}</span>
        <span class="reply-counter-number gray--text" v-else>{{ item.RepliesCount }}</span>
        <v-icon class="reply-counter-icon" :class="`${notificationItemNotSeenRepliesCount(item) ? 'red--text' : ''}`" title="Toggle Replies"
          >mdi-comment</v-icon
        ></span
      >
      <v-icon v-if="hasReply(item)" title="Reply to Notification" class="" @click="toggleReplyViewItems(item)"> mdi-reply </v-icon>
    </div>
    <div v-if="item.showReplyBox" class="reply-box">
      <TextAreaField
        clearable
        label="Reply to Notification"
        prepend-inner-icon="mdi-pencil"
        rows="5"
        v-model="item.replyText"
        @keydown.enter.stop
        @keydown.space.stop
      />
      <v-btn class="detailButton secondary_btn" @click="sendReply(item)" :loading="item.itemLoading">Send Reply</v-btn>
    </div>
  </v-list-item-content>
</template>
<script lang="ts">
import Utils from "../../utils/Utils";
import { defineComponent } from "vue";
import NotificationService from "../../services/NotificationService";
import { NotificationType } from "../../definitions/constants";
import NotificationIconContent from "./NotificationIconContent.vue";
import NotificationRedirectContent from "./NotificationRedirectContent.vue";
import NotificationOwnerContent from "./NotificationOwnerContent.vue";

export default defineComponent({
  components: {
    NotificationIconContent,
    NotificationRedirectContent,
    NotificationOwnerContent,
  },
  props: {
    item: {
      type: Object,
    },
    userId: {
      type: String,
    },
    isClient: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    isTeamMember: {
      type: Boolean,
      default: false,
    },
    hasAutoUpdate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      notificationTypes: NotificationType,
    };
  },
  methods: {
    isNotificationCreator(item: any) {
      return item.CreatorUserId === this.userId;
    },
    isReplyCreator(item: any, reply: any) {
      if (this.isNotificationCreator(item)) {
        return reply.SentByCreator;
      } else {
        return !reply.SentByCreator;
      }
    },
    getTimeAgo(date: Date) {
      return Utils.toVsDateFormat(date);
    },
    showAsCreator(notificationItem: any, reply: any) {
      if (this.isNotificationCreator(notificationItem)) {
        return !reply.SentByCreator;
      }
      return reply.SentByCreator;
    },
    hasReply(item: any) {
      return (
        item.CreatorUserId &&
        (item.Type == NotificationType.FeedBack ||
          item.Type == NotificationType.Kudos ||
          item.Type == NotificationType.Tip ||
          item.Type == NotificationType.Info)
      );
    },
    async withdrawNotification(item: any) {
      try {
        const result = await NotificationService.withdrawNotificationItem(item.id, this.isClient);
        if (result.status) {
          this.$emit("successRaise", result.message);
          this.$emit("initNotifications");
        } else {
          this.$emit("errorRaise", result.message);
        }
      } catch (error) {
        this.$emit("errorRaise", "Error in operation: " + error);
      } finally {
        this.$emit("initNotSeenNotificationsCount");
      }
    },

    notificationItemNotSeenRepliesCount(item: any) {
      let notSeenReplies = 0;
      if (!item.showReplyBox) {
        item.Replies.forEach((reply: any) => {
          if (!this.isReplySeen(item, reply)) {
            notSeenReplies++;
          }
        });
      }
      return notSeenReplies;
    },
    isReplySeen(notificationItem: any, reply: any) {
      return reply.Status === "Seen" || reply.SentByCreator === this.isNotificationCreator(notificationItem);
    },
    async toggleReplyViewItems(item: any) {
      this.toggleReplyInsertion(item);
      if (item.showReplyItems) {
        this.updateItemsVersion();

        item.showReplyItems = false;
      } else {
        await this.makeSeenReply(item);
        this.updateItemsVersion();

        item.showReplyItems = true;
      }
    },
    updateItemsVersion() {
      // this.$emit("updateItemsVersion");
    },
    toggleReplyInsertion(item: any) {
      this.updateItemsVersion();
      if (item.showReplyBox) {
        item.showReplyBox = false;
      } else {
        item.showReplyBox = true;
      }
    },
    async sendReply(item: any) {
      let currentNotificationIdToShow = "";
      const replyModel = {
        notificationItemId: item.id,
        text: item.replyText,
        sentByCreator: this.isNotificationCreator(item),
      };
      try {
        item.itemLoading = true;

        const result = await NotificationService.reply(replyModel.notificationItemId, replyModel.text, replyModel.sentByCreator, this.isClient);
        if (result.status) {
          this.$emit("successRaise", result.message);
          if (!this.hasAutoUpdate) {
            item.Replies.push(result.data);
          } else {
            currentNotificationIdToShow = item.id;
          }
          item.replyText = "";
        } else {
          this.$emit("errorRaise", result.message);
        }
      } catch (error) {
        this.$emit("errorRaise", "Error in operation: " + error);
      } finally {
        item.itemLoading = false;
        this.$emit("initNotSeenNotificationsCount", currentNotificationIdToShow);
      }
    },
    async makeSeenReply(item: any) {
      let currentNotificationIdToShow = "";

      try {
        item.itemLoading = true;

        const result = await NotificationService.makeSeenReply(item.id, this.isNotificationCreator(item), this.isClient);
        if (result.status) {
          const seenItems = result.data;
          if (!this.hasAutoUpdate) {
            item.Replies.forEach((reply: any) => {
              if (seenItems.includes(reply.Id)) {
                reply.Status = "Seen";
              }
            });
          } else {
            currentNotificationIdToShow = item.id;
          }
        } else {
          this.$emit("errorRaise", result.message);
        }
      } catch (error) {
        this.$emit("errorRaise", "Error in operation: " + error);
      } finally {
        item.itemLoading = false;
        this.$emit("initNotSeenNotificationsCount", currentNotificationIdToShow);
      }
    },
  },
});
</script>
<style scoped lang="scss">
@import "node_modules/shared-components/assets/colors.scss";

.item-header {
  font-size: 13px;
  font-weight: bold;
}

.item-header .owner-label {
  font-size: 10px;
  font-weight: normal;
}

.item-content {
  font-size: 12px;
  margin-top: 6px;
}

.item-replies .reply-item {
  background-color: $pit_crew_uniform;
  color: #fff;
  font-size: 13px;
  padding: 5px 11px;
  border-radius: 0px 7px 7px 7px;
  margin-bottom: 2px;
  width: fit-content;
  min-width: 50%;
}

.item-replies .reply-item.provider {
  float: inline-end;
  background-color: $text_color !important;
  margin-top: 1px;
  border-radius: 7px 7px 0px 7px !important;
}

.item-replies .reply-item .owner-label {
  font-size: 9.5px;
  font-weight: normal;
}

.reply-actions .reply-counter-number {
  position: relative;
  top: -2px;
  left: 15.5px;
  font-size: 11px;
  font-weight: bold;
  z-index: 1;
}

.reply-actions .reply-counter-number.gray--text {
  color: $text_color;
}

.reply-actions .reply-counter-icon.red--text {
  color: $fire;
}
</style>
